import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchEventSource } from '@microsoft/fetch-event-source';

function Chat({ user, userStatus, onMessageSent }) {
  const { characterId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  // Определяем язык: из URL параметров, localStorage или по умолчанию 'en'
  const urlParams = new URLSearchParams(window.location.search);
  const urlLanguage = urlParams.get('lang');
  const savedLanguage = localStorage.getItem('preferredLanguage');
  const [language] = useState(urlLanguage || savedLanguage || 'en');

  const [character, setCharacter] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCharacterInfo = useCallback(async () => {
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'production'
        ? 'https://hotbump.online'
        : 'http://localhost:8013';
      const response = await axios.get(`${API_BASE_URL}/api/characters`);
      const characters = response.data.characters;
      
      if (characters[characterId]) {
        setCharacter(characters[characterId]);
      } else {
        setError('Персонаж не найден');
      }
    } catch (error) {
      console.error('Failed to load character:', error);
      setError('Ошибка загрузки персонажа');
    } finally {
      setLoading(false);
    }
  }, [characterId]);

  const loadChatHistory = useCallback(async () => {
    if (!user?.initData) return;
    
    try {
      const response = await axios.get(`/api/web/chat/history/${characterId}?initData=${encodeURIComponent(user.initData)}&lang=${language}`);
      setMessages(response.data.history || []);
    } catch (error) {
      console.error('Failed to load chat history:', error);
      // Не показываем ошибку, просто начинаем с пустой истории
      setMessages([]);
    }
  }, [characterId, user?.initData, language]);

  useEffect(() => {
    const initializeChat = async () => {
      await loadCharacterInfo();
      await loadChatHistory();
    };
    
    initializeChat();
  }, [characterId, loadCharacterInfo, loadChatHistory]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !user?.initData || isWaitingForResponse) return;

    const message = inputMessage.trim();
    setInputMessage('');
    setIsWaitingForResponse(true);
    setIsTyping(true);
    setError(null);

    // Не нужно перезагружать историю перед отправкой - она загрузится после завершения стриминга

    try {
      // Создаем временное сообщение для отображения потока
      let streamingMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        streaming: true
      };
      
      setMessages(prev => [...prev, streamingMessage]);

      // Используем потоковый API для эффекта печати
      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? 'https://hotbump.online'
        : 'http://localhost:8010';
      
      await fetchEventSource(`${API_BASE_URL}/api/web/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.initData}`,
        },
        body: JSON.stringify({
          character_id: characterId,
          message: message
        }),
        
        onmessage(event) {
          const chunk = event.data;
          if (chunk) {
            // Проверяем, является ли это ошибкой
            if (chunk.startsWith('ERROR:')) {
              const errorMessage = chunk.slice(6); // Убираем префикс "ERROR:"
              console.error('Chat streaming error:', errorMessage);
              setError(errorMessage);
              setIsTyping(false);
              setIsWaitingForResponse(false);
              // Удаляем временное сообщение
              setMessages(prev => prev.filter(msg => !msg.streaming));
              return;
            }

            // Добавляем символ к текущему сообщению (backend уже делает typing effect)
              setMessages(prev => {
                const updated = [...prev];
                const lastMessage = updated[updated.length - 1];
              if (lastMessage && lastMessage.role === 'assistant' && lastMessage.streaming) {
                lastMessage.content += chunk;
                return [...updated];
                }
                return updated;
              });
          }
        },
        
        onerror(error) {
          console.error('SSE error:', error);
          setError('Ошибка при отправке сообщения');
          setIsTyping(false);
          setIsWaitingForResponse(false);
        },
        
        onclose() {
            // Завершаем потоковое сообщение
          setMessages(prev => {
            const updated = [...prev];
            const lastMessage = updated[updated.length - 1];
            if (lastMessage && lastMessage.role === 'assistant' && lastMessage.streaming) {
              lastMessage.streaming = false;
            }
            return updated;
            });
          
            // Перезагружаем историю чата после завершения стриминга
            loadChatHistory();

            // Обновляем статус пользователя
            if (onMessageSent) {
              onMessageSent();
            }

            // Сбрасываем индикаторы
            setIsTyping(false);
            setIsWaitingForResponse(false);
        }
      });

    } catch (error) {
      console.error('Chat error:', error);
      
      // Удаляем неполное сообщение ассистента
      setMessages(prev => prev.filter(msg => !msg.streaming));
      
      if (error.status === 429) {
        const errorMessage = error.message || 'Превышен лимит сообщений. Попробуйте позже.';
        if (errorMessage.includes('подождите 10 секунд')) {
          setError('⏰ Пожалуйста, подождите 10 секунд перед отправкой следующего сообщения');
        } else {
          setError('Превышен дневной лимит сообщений. Оформите подписку, чтобы продолжить.');
        }
      } else {
        setError('Ошибка при отправке сообщения. Попробуйте еще раз.');
      }
      
      setIsTyping(false);
      setIsWaitingForResponse(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка чата...</p>
        </div>
      </div>
    );
  }

  if (error && !character) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Ошибка</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/characters')}
            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
          >
            Вернуться к выбору персонажей
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-md p-4 flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/characters')}
            className="mr-4 text-gray-500 hover:text-gray-700"
          >
            ←
          </button>
          <div className="w-10 h-10 bg-purple-400 rounded-full flex items-center justify-center mr-3 overflow-hidden">
            {character?.avatar ? (
              <img 
                src={`${process.env.NODE_ENV === 'production' ? 'https://hotbump.online' : 'http://localhost:8010'}${character.avatar}`}
                alt={character.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
            ) : null}
            <span className="text-white text-lg" style={{display: character?.avatar ? 'none' : 'block'}}>👤</span>
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-800">
              {character?.name}
            </h1>
            <p className="text-sm text-gray-500">
              {character?.description}
            </p>
          </div>
        </div>
        
        {/* User status */}
        {userStatus && (
          <div className="text-sm text-gray-500">
            {userStatus.daily_messages} / {userStatus.daily_limit}
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mx-4 mt-4 rounded">
          <div className="mb-2">{error}</div>
          {error.includes('лимит') && (
            <button
              onClick={() => {
                const link = 'https://t.me/hotbump_bot';
                if (window.Telegram?.WebApp?.openTelegramLink) {
                  window.Telegram.WebApp.openTelegramLink(link);
                } else {
                  window.location.href = link;
                }
              }}
              className="mt-2 bg-burgundy-700 hover:bg-burgundy-600 text-white font-bold py-2 px-4 rounded"
            >
              Оформить подписку
            </button>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 mt-8">
            <p>Начните общение с {character?.name}</p>
          </div>
        )}
        
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} items-start space-x-2`}
          >
            {/* Аватарка персонажа (только для сообщений ассистента) */}
            {message.role === 'assistant' && character?.avatar && (
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                <img 
                  src={`${process.env.NODE_ENV === 'production' ? 'https://hotbump.online' : 'http://localhost:8010'}${character.avatar}`}
                  alt={character.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <span className="text-lg" style={{display: 'none'}}>👤</span>
              </div>
            )}
            
            <div
              className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                message.role === 'user'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-800 shadow-md'
              }`}
            >
              <p className="text-sm">{message.content}</p>
              {message.imageUrl && (
                <div className="mt-2">
                  <img 
                    src={message.imageUrl} 
                    alt="Character"
                    className="max-w-full h-auto rounded-lg"
                    onError={(e) => {
                      console.error('Failed to load image:', message.imageUrl);
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              {message.streaming && (
                <span className="inline-block w-2 h-4 bg-current ml-1 animate-pulse" style={{
                  animation: 'blink 1s infinite',
                  verticalAlign: 'middle'
                }}>▌</span>
              )}
            </div>
          </div>
        ))}
        
        {/* Typing indicator */}
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white text-gray-800 shadow-md px-4 py-2 rounded-lg">
              <span className="text-sm">Печатает...</span>
              <span className="inline-block w-2 h-2 bg-gray-400 rounded-full animate-pulse ml-1"></span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className={`border-t p-4 transition-colors ${
        isWaitingForResponse ? 'bg-gray-50' : 'bg-white'
      }`}>
        {isWaitingForResponse && (
          <div className="text-center text-sm text-gray-500 mb-2">
            ⏳ Ожидаем ответ от {character?.name}...
          </div>
        )}
        <div className="flex space-x-2">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isWaitingForResponse ? "Ожидание ответа..." : `Напишите ${character?.name}...`}
            className={`flex-1 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-colors ${
              isWaitingForResponse 
                ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed' 
                : 'border-gray-300 focus:ring-blue-500'
            }`}
            disabled={isWaitingForResponse}
          />
          <button
            onClick={sendMessage}
            disabled={!inputMessage.trim() || isWaitingForResponse}
            className={`px-4 py-2 rounded-lg transition-colors ${
              isWaitingForResponse
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            {isWaitingForResponse ? 'Ожидание...' : 'Отправить'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Chat;

